import { useQuery } from '@tanstack/react-query';
import { Package, ChevronsUpDown, Check, X } from 'lucide-react';
import { useState, useCallback, useRef, useEffect } from 'react';
import { 
  searchArticles, 
  fetchArticleById,
  type Article,
  type ArticleWithRelevance,
} from '@/core/api';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { cn } from '@/shared/lib/utils';

interface ArticleSelectorProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
  areaId?: string | null;
  lineId?: string | null;
  allowClear?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Efficient article selector optimized for large datasets (100k+ records).
 * - Server-side search only (no upfront data loading)
 * - Debounced search input
 * - Fetches selected item separately for display
 */
export const ArticleSelector = ({
  value,
  onValueChange,
  areaId,
  lineId,
  allowClear = false,
  placeholder = 'Select article',
  disabled = false,
  className,
}: ArticleSelectorProps) => {
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<NodeJS.Timeout>();

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 200);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [search]);

  // Fetch selected article for display (only when we have a value)
  const { data: selectedArticle } = useQuery({
    queryKey: ['article', value],
    queryFn: () => {
      if (!value) return null;
      return fetchArticleById(value);
    },
    enabled: !!value,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Server-side search - only when user types
  const { data: searchResults = [], isFetching } = useQuery({
    queryKey: ['articles-search', selectedTenantId, selectedFactoryId, debouncedSearch, areaId, lineId],
    queryFn: () => {
      if (!selectedFactoryId || debouncedSearch.length < 1) return [];
      return searchArticles(selectedFactoryId, debouncedSearch, areaId, lineId);
    },
    enabled: !!selectedFactoryId && debouncedSearch.length >= 1,
    staleTime: 30 * 1000, // Cache search results for 30 seconds
  });

  const handleSelect = useCallback((articleId: string) => {
    onValueChange(articleId);
    setOpen(false);
    setSearch('');
  }, [onValueChange]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onValueChange(null);
  }, [onValueChange]);

  const formatArticleDisplay = (article: Article | ArticleWithRelevance) => {
    if (article.name) {
      return `${article.code} - ${article.name}`;
    }
    return article.code;
  };

  if (!selectedFactoryId) {
    return (
      <Badge variant="secondary" className="text-xs">
        Select factory first
      </Badge>
    );
  }

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "w-full justify-between font-normal",
              !value && "text-muted-foreground"
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Package className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {selectedArticle ? formatArticleDisplay(selectedArticle) : placeholder}
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {allowClear && value && (
                <X
                  className="h-4 w-4 opacity-50 hover:opacity-100"
                  onClick={handleClear}
                />
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by code or name..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {debouncedSearch.length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Type to search articles...
                </div>
              )}
              {debouncedSearch.length > 0 && searchResults.length === 0 && !isFetching && (
                <CommandEmpty>No articles found.</CommandEmpty>
              )}
              {isFetching && debouncedSearch.length > 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Searching...
                </div>
              )}
              {searchResults.length > 0 && (
                <CommandGroup>
                  {searchResults.map((article) => (
                    <CommandItem
                      key={article.id}
                      value={article.id}
                      onSelect={handleSelect}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          value === article.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{formatArticleDisplay(article)}</span>
                        {article.description && (
                          <span className="text-xs text-muted-foreground truncate">
                            {article.description}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
